export const IMPERSONATION_CHANGED_EVENT = "enkey:impersonation-changed";
const STORAGE_KEY = "enkey.debug.impersonation";

export type ImpersonationSession = {
  token: string;
  expiresAt: number;
  target: {
    id: string;
    name: string;
    email?: string;
    role: "admin" | "staff" | "client";
    legacyRole?: string;
  };
};

type ImpersonationErrorPayload = {
  error?: string | { message?: string };
};

function invalidImpersonationResponseMessage(response: Response) {
  const requestId = response.headers.get("x-request-id");
  return [
    "Il server ha restituito una pagina non valida al posto della risposta API. Ricarica il CRM e riprova.",
    requestId ? `Riferimento richiesta: ${requestId}.` : "",
  ]
    .filter(Boolean)
    .join(" ");
}

export async function readImpersonationResponse(response: Response) {
  const responseText = await response.text();
  let payload: ImpersonationSession | ImpersonationErrorPayload;
  try {
    const parsed = JSON.parse(responseText) as unknown;
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      throw new Error("INVALID_IMPERSONATION_RESPONSE");
    }
    payload = parsed as ImpersonationSession | ImpersonationErrorPayload;
  } catch {
    throw new Error(invalidImpersonationResponseMessage(response));
  }

  if (!response.ok || !("token" in payload)) {
    const error = "error" in payload ? payload.error : undefined;
    throw new Error(
      (typeof error === "string" ? error : error?.message) ||
        "Impossibile avviare la simulazione",
    );
  }
  return payload;
}

export function getImpersonationSession(): ImpersonationSession | null {
  if (typeof window === "undefined") return null;
  try {
    const parsed = JSON.parse(
      window.sessionStorage.getItem(STORAGE_KEY) || "null",
    ) as ImpersonationSession | null;
    if (!parsed?.token || !parsed.target?.id) return null;
    if (parsed.expiresAt <= Date.now()) {
      clearImpersonationSession();
      return null;
    }
    return parsed;
  } catch {
    clearImpersonationSession();
    return null;
  }
}

export function impersonationHeaders(): Record<string, string> {
  const session = getImpersonationSession();
  return session ? { "x-enkey-impersonation": session.token } : {};
}

export function clearImpersonationSession() {
  if (typeof window === "undefined") return;
  window.sessionStorage.removeItem(STORAGE_KEY);
  window.dispatchEvent(new Event(IMPERSONATION_CHANGED_EVENT));
}

export async function startImpersonation(targetUserId: string) {
  const response = await fetch("/api/admin/impersonation/start", {
    method: "POST",
    credentials: "same-origin",
    cache: "no-store",
    headers: {
      accept: "application/json",
      "content-type": "application/json",
    },
    body: JSON.stringify({ targetUserId }),
  });
  const payload = await readImpersonationResponse(response);
  window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  window.dispatchEvent(new Event(IMPERSONATION_CHANGED_EVENT));
  return payload;
}

export async function stopImpersonation() {
  const session = getImpersonationSession();
  try {
    if (session) {
      await fetch("/api/admin/impersonation/stop", {
        method: "POST",
        credentials: "same-origin",
        cache: "no-store",
        headers: {
          accept: "application/json",
          "content-type": "application/json",
          "x-enkey-impersonation": session.token,
        },
        body: "{}",
      });
    }
  } finally {
    clearImpersonationSession();
  }
}
